import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { LoadingIndicator } from "../../components/LoadingIndicator";
import type { SignWord } from "../../components/SignPlayer";
import { apiFetch } from "../../lib/api-client";

export const Route = createFileRoute("/_app/sign-review")({
  component: SignReviewPage,
});

type SignLanguageItem = {
  id: string;
  text: string;
  words: SignWord[];
  createdAt: string;
};

const fetchSignItems = (): Promise<SignLanguageItem[]> =>
  apiFetch<SignLanguageItem[]>("/sign-items");

const deleteSignItem = (id: string): Promise<{ id: string }> =>
  apiFetch<{ id: string }>(`/sign-items/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });

function SignReviewPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["sign-items"],
    queryFn: fetchSignItems,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSignItem,
    onSuccess: (_, id) => {
      qc.setQueryData<SignLanguageItem[]>(["sign-items"], (prev) =>
        prev ? prev.filter((it) => it.id !== id) : prev,
      );
    },
  });

  const onDelete = (item: SignLanguageItem) => {
    if (!window.confirm(`"${item.text}" を削除しますか？`)) return;
    deleteMutation.mutate(item.id);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">手話復習</h1>
        <p className="text-sm text-neutral-600 mt-1">
          書き起こした文を、手話でどう表現するか見返します
        </p>
      </div>

      {isLoading && <LoadingIndicator label="読み込み中..." />}
      {error && (
        <p className="text-sm text-red-600">{(error as Error).message}</p>
      )}

      {data && data.length === 0 && (
        <div className="rounded-xl border border-dashed border-neutral-300 p-6 text-center text-neutral-500">
          まだ何もありません
        </div>
      )}

      {data && data.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {data.map((item) => (
            <div
              key={item.id}
              className="rounded-xl border border-neutral-200 bg-white p-4 relative"
            >
              <button
                type="button"
                onClick={() => onDelete(item)}
                disabled={deleteMutation.isPending}
                className="absolute top-2 right-2 rounded-full bg-white/90 hover:bg-red-500 hover:text-white text-neutral-600 w-8 h-8 flex items-center justify-center text-sm shadow border border-neutral-200 transition-colors disabled:opacity-50"
                aria-label="削除"
                title="削除"
              >
                ×
              </button>
              <Link
                to="/dictation/sign"
                search={{ id: item.id }}
                className="block pr-8"
              >
                <p className="text-sm font-medium">{item.text}</p>
                <p className="text-xs text-neutral-500 mt-1">
                  {item.words.filter((w) => w.matched).length} /{" "}
                  {item.words.length} 単語
                </p>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
